<script lang="ts">
  // Left column of the introduction (tablet and up): logo, the step's
  // illustration and the list of steps.
  import { fade, fly } from 'svelte/transition';
  import { Check } from '@lucide/svelte';
  import { t } from '../i18n.svelte';
  import { initials } from '../format';
  import Logo from '../shell/Logo.svelte';
  import Illustration from './Illustration.svelte';
  import { flow, STEPS } from './state.svelte';
</script>

<aside class="art" aria-hidden="true">
  <div class="logo"><Logo size={26} /></div>
  <div class="stage">
    {#key flow.index}
      <div class="item" in:fly={flow.stepIn} out:fade={flow.stepOut}>
        <Illustration kind={flow.step} size={300} color={flow.clientColor} initials={flow.clientName ? initials(flow.clientName) : ''} />
      </div>
    {/key}
  </div>
  <ol class="steps">
    {#each STEPS as key, i (key)}
      <li class:current={i === flow.index} class:done={i < flow.index}>
        <span class="num">{#if i < flow.index}<Check size={11} strokeWidth={3} />{:else}{i + 1}{/if}</span>
        <span>{t(`onb.${key}.kicker`, { name: '' }).replace(/!$/, '')}</span>
      </li>
    {/each}
  </ol>
</aside>

<style>
  .art {
    position: relative;
    display: flex;
    flex-direction: column;
    padding: var(--space-6);
    background:
      radial-gradient(120% 70% at 20% 0%, color-mix(in srgb, var(--brand) 16%, transparent), transparent 60%),
      radial-gradient(90% 60% at 100% 100%, color-mix(in srgb, var(--accent) 14%, transparent), transparent 60%),
      var(--surface-2);
    border-right: 1px solid var(--border);
    overflow: hidden;
  }
  /* Dotted grid fading out towards the bottom */
  .art::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(color-mix(in srgb, var(--brand) 14%, transparent) 1px, transparent 1px);
    background-size: 18px 18px;
    mask-image: linear-gradient(180deg, var(--text) 0%, transparent 70%);
    -webkit-mask-image: linear-gradient(180deg, var(--text) 0%, transparent 70%);
    pointer-events: none;
  }
  .logo, .stage, .steps { position: relative; }
  .stage { flex: 1; display: grid; place-items: center; min-height: 0; }
  .item { grid-area: 1 / 1; display: grid; place-items: center; width: 100%; }

  .steps { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: var(--space-0_5); }
  .steps li { display: flex; align-items: center; gap: var(--space-2_5); padding: var(--space-1) 0; font-size: var(--text-sm); color: var(--text-3); transition: color var(--dur); }
  .steps .current { color: var(--text); font-weight: var(--weight-semibold); }
  .steps .done { color: var(--text-2); }
  .num {
    display: grid;
    place-items: center;
    flex-shrink: 0;
    width: var(--space-5);
    height: var(--space-5);
    border: 1.5px solid var(--border-strong);
    border-radius: var(--radius-full);
    background: var(--surface);
    font-size: var(--text-2xs);
    font-weight: var(--weight-bold);
    transition: background var(--dur), border-color var(--dur), color var(--dur);
  }
  .current .num { border-color: var(--brand); color: var(--brand); box-shadow: 0 0 0 3px var(--brand-soft); }
  .done .num { background: var(--brand); border-color: var(--brand); color: var(--on-brand); }

  @media (max-width: 900px) {
    .steps { display: none; }
  }
  @media (max-width: 720px) {
    .art { display: none; }
  }
</style>
